import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell } from "recharts";
import { ChartCard } from "@/components/ChartCard";
import AiInsightsPanel from "@/components/AiInsightsPanel";
import DrillDownModal from "@/components/DrillDownModal";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useFilters } from "@/state/FilterContext";
import { formatTRY } from "@/lib/api";
import { CHART_PALETTE } from "@/lib/palette";

const Tip = ({ active, payload, label }) =>
  !active || !payload?.length ? null : (
    <div className="bg-white border border-slate-200 rounded-md shadow-md px-3 py-2 text-xs">
      <div className="font-semibold text-slate-800 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-slate-600">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color || p.fill }} />
          <span>{p.name}:</span>
          <span className="font-semibold text-slate-900 tabular-nums">{formatTRY(p.value)}</span>
        </div>
      ))}
    </div>
  );

export default function Costs() {
  const { data: matrix } = useDashboardData("/dashboard/cost-by-carrier-location");
  const { data: ratio } = useDashboardData("/dashboard/source-location-ratio");
  const { meta } = useFilters();
  const [drill, setDrill] = useState({ open: false, title: "", overrides: {} });

  const locCode = (name) => meta.locations?.find((l) => l.name === name)?.code || name;
  const carCode = (name) => meta.carriers?.find((c) => c.name === name)?.code || name;
  const openDrill = (location, carrier) => {
    const overrides = {};
    if (location) overrides.location = locCode(location);
    if (carrier) overrides.carrier = carCode(carrier);
    const title = [location, carrier].filter(Boolean).join(" × ") || "Detay";
    setDrill({ open: true, title: `${title} – Sevkiyatlar`, overrides });
  };

  const carriers = matrix?.carriers || [];
  const rows = matrix?.rows || [];
  const flat = matrix?.flat || [];

  // Find max for heatmap intensity
  const maxCost = Math.max(1, ...flat.map((f) => f.cost));

  return (
    <div className="space-y-6">
      <AiInsightsPanel />

      <ChartCard
        testid="chart-cost-matrix"
        title="Nakliyeci × Kaynak Lokasyon Maliyet Matrisi"
        subtitle="Her hücre, ilgili nakliyeci için o lokasyondan kaynaklanan toplam nakliye maliyetidir (₺)"
      >
        <ResponsiveContainer width="100%" height={420}>
          <BarChart data={rows} margin={{ left: 8, right: 8, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="location" tick={{ fontSize: 11, fill: "#64748B" }} angle={-15} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip content={<Tip />} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            {carriers.map((c, i) => (
              <Bar key={c} dataKey={c} name={c} fill={CHART_PALETTE[i % CHART_PALETTE.length]} radius={[3, 3, 0, 0]}
                cursor="pointer"
                onClick={(d) => d?.location && openDrill(d.location, c)} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        testid="chart-cost-heatmap"
        title="Maliyet Yoğunluk Haritası"
        subtitle="Daha koyu hücreler daha yüksek toplam nakliye maliyetini gösterir"
      >
        <div className="overflow-x-auto" data-testid="heatmap-wrap">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">
                  Kaynak Lokasyon
                </th>
                {carriers.map((c) => (
                  <th key={c} className="p-2 text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold text-center min-w-[120px]">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.location} className="border-t border-slate-100">
                  <td className="p-2 font-medium text-slate-800">{r.location}</td>
                  {carriers.map((c) => {
                    const v = r[c] || 0;
                    const intensity = v / maxCost;
                    const bg = `rgba(0, 112, 242, ${0.06 + intensity * 0.72})`;
                    const fg = intensity > 0.55 ? "#FFFFFF" : "#1E293B";
                    return (
                      <td
                        key={c}
                        data-testid={`heatmap-${r.location}-${c}`}
                        onClick={() => openDrill(r.location, c)}
                        className="p-2 text-center font-semibold tabular-nums rounded-sm cursor-pointer hover:ring-2 hover:ring-[#0070F2] hover:ring-inset transition"
                        style={{ background: bg, color: fg }}
                      >
                        {formatTRY(v)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <ChartCard
        testid="chart-source-cost"
        title="Kaynak Lokasyona Göre Toplam Maliyet"
        subtitle="Toplam nakliye maliyeti, lokasyon bazında"
      >
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={ratio || []} margin={{ left: 8, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="location" tick={{ fontSize: 11, fill: "#64748B" }} angle={-15} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip content={<Tip />} />
            <Bar dataKey="cost" name="Toplam Maliyet" radius={[4, 4, 0, 0]}
              cursor="pointer"
              onClick={(d) => d?.location && openDrill(d.location, null)}>
              {(ratio || []).map((_, i) => (
                <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <DrillDownModal
        open={drill.open}
        onOpenChange={(o) => setDrill((d) => ({ ...d, open: o }))}
        title={drill.title}
        overrides={drill.overrides}
      />
    </div>
  );
}
